import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { api } from './lib/api';
import { formatCv, formatEntry, formatSection } from './lib/copyText';
import { sortProgramsStreamFirst } from './lib/sortPrograms';
import { IconUp, IconDown, IconClose, IconCheck, IconCopy, IconAdd, IconBack, IconTrash, IconDownload } from './components/icons';
import { Drawer } from './components/Drawer';
import { entryLabel } from './components/CvFullDrawer';
// cc-11 round 14 (Sol blocker 2) — the standing quantity note the stage-1 prompt promises under EVERY quantity
// question. This surface renders /api/classify output, so the promise has to hold here too.
import { isQuantityAsk, QUANTITY_HINT_COPY } from './quantityHint';

// CV Workspace (the review-feature consolidation, P3) — grown from CvPane's paper aesthetic into the ONE
// full-page place you see AND edit your CV. Replaces the prior admin-grade review surface (now retired).
// Every write goes through the EXISTING user-word endpoints (PATCH entries/:id, PATCH bullets/:id, DELETEs,
// tags endpoints); the only new endpoint is POST /api/entries/:id/bullets ("+ Add a line" — user text,
// deletion-only cleanup, craft-gated). No composed text anywhere; a 402 opens the paywall and KEEPS the
// user's words on screen. Tailoring — previously buried in an admin-grade panel — is PROMOTED here to a
// visible rail (main paper column
// + right rail; the rail stacks below the paper on narrow viewports, useIsNarrow(900) — same threshold the
// guided-authoring split screen uses).

interface Gate { gate_id: string; status: 'pass' | 'flag'; diagnosis: string; question: string }
interface ClassifyResult { overall: 'pass' | 'needs_attention'; ladder: Gate; integrity: Gate[] }
interface MBullet { id: string; text: string }
// `section` is the entry's TAXONOMY section id (e.g. 'research-and-scholarly-activity'), NOT the enclosing
// MSection's id — output sections are merged display headings with synthetic `out:<label>` ids. See the Check
// handler below: the classifier's section-keyed routing only matches taxonomy ids.
interface MEntry { id: string; section: string; archetype: 'experience' | 'credential'; title: string | null; organization: string | null; dateText: string; bullets: MBullet[] }
interface MSection { id: string; name: string; entries: MEntry[] }
interface Model { contact: { name: string; email: string; phone: string; city: string }; sections: MSection[] }
interface Pending { kind: 'bullet' | 'entry'; id: string }
interface DimTag { id: string; name: string; plain_language: string; source: string }
interface EntryTags { entry_id: string; dimensions: DimTag[] }
interface DimRef { id: string; name: string; plain_language: string }
interface RecoverItem { dimension_id: string; lessLine: string; where: string; recoverQuestion: string; honesty: string; inform: string }
interface CoverageRead { hasContent: boolean; lead: string; recover: RecoverItem[] }
interface Program { program_id: string; name: string; university: string | null; site: string | null; stream: string | null }
interface LensEntry { entry_id: string; relevance: number; tagged: boolean; foregrounded: boolean }
interface RelatedEntry { entry_id: string; title: string }
interface CharacterTheme { theme_id: string; plain_language: string; note: string; related: RelatedEntry[]; prevalence: number; prevalence_basis?: string }
// short_label — the enumerable noun phrase for the common line (server-side THEME_SHORT_LABELS, one entry per
// released theme). Optional so a response from an older worker still renders (falls back to plain_language).
interface CommonTheme { theme_id: string; plain_language: string; short_label?: string }
interface CharacterResp { retrieved_at: number | null; release_version: string | null; themes: CharacterTheme[]; common: CommonTheme[]; specialty_id?: string; specialty_label?: string }
interface SectionRef { id: string; name: string; sort_order: number; archetype: 'experience' | 'credential' }
interface EntryFull { id: string; cv_id: string; section: string; title: string | null; organization: string | null; start_date: string | null; end_date: string | null; date_text: string | null; sort_order: number }
interface EntryDraft { title: string; organization: string; start_date: string; end_date: string; date_text: string; section: string }

// Viewport hook — mirrors App.tsx's useIsNarrow (matchMedia guarded for jsdom).
function useIsNarrow(maxWidth: number): boolean {
  const mqOf = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`)
      : null;
  const [narrow, setNarrow] = useState(() => mqOf()?.matches ?? false);
  useEffect(() => {
    const mq = mqOf();
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, [maxWidth]);
  return narrow;
}

export function CvWorkspace({ cvId, onBack, onAddForGap, onPaywall, reloadKey = 0 }: {
  cvId: string; onBack: () => void; onAddForGap?: (dimensionId: string) => void; onPaywall?: (cvId: string) => void; reloadKey?: number;
}) {
  const narrow = useIsNarrow(900);

  const gated = (res: { status: number; data: unknown }): boolean => {
    if (res.status === 402 && (res.data as { reason?: string } | null)?.reason === 'needs_paid_cv') { onPaywall?.(cvId); return true; }
    return false;
  };
  const addForGap = (dimensionId: string) => (onAddForGap ? onAddForGap(dimensionId) : onBack());

  const [model, setModel] = useState<Model | null>(null);
  const [sections, setSections] = useState<SectionRef[]>([]);

  // Bullet edit/check
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [diag, setDiag] = useState<ClassifyResult | null>(null);
  // Add-a-line
  const [addingBulletFor, setAddingBulletFor] = useState<string | null>(null);
  const [addDraft, setAddDraft] = useState('');
  const [addNote, setAddNote] = useState('');
  // Entry field edit
  const [entryEdit, setEntryEdit] = useState<string | null>(null);
  const [entryDraft, setEntryDraft] = useState<EntryDraft>({ title: '', organization: '', start_date: '', end_date: '', date_text: '', section: '' });
  const [entryArchetype, setEntryArchetype] = useState<'experience' | 'credential' | null>(null);

  // Polish B2 mobile tranche — the tailoring rail becomes a Drawer under 900px (was stacked below the whole CV).
  const [tailorOpen, setTailorOpen] = useState(false);

  const [pendingDel, setPendingDel] = useState<Pending | null>(null);
  const [tags, setTags] = useState<Record<string, DimTag[]>>({});
  const [dims, setDims] = useState<DimRef[]>([]);
  const [inferring, setInferring] = useState(false);
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [cov, setCov] = useState<CoverageRead | null>(null);

  const [available, setAvailable] = useState<Program[]>([]);
  const [cvStream, setCvStream] = useState<string | null>(null); // this CV's OWN stream (img/cmg) — for stream-first program-picker ordering
  const [targets, setTargets] = useState<Program[]>([]);
  const [tlFormat, setTlFormat] = useState<'docx' | 'pdf'>('docx');
  const [tlCrafted, setTlCrafted] = useState(false);
  const [activeProg, setActiveProg] = useState<string | null>(null);
  const [lens, setLens] = useState<LensEntry[] | null>(null);
  const [progCov, setProgCov] = useState<CoverageRead | null>(null);
  const [progChar, setProgChar] = useState<CharacterResp | null>(null);

  // Canonical (untailored) download — own state, promoted into the toolbar.
  const [exportFormat, setExportFormat] = useState<'docx' | 'pdf'>('docx');
  const [exportCrafted, setExportCrafted] = useState(false);
  const [exportNote, setExportNote] = useState('');

  // Copy-out: quiet plain-text copy controls (section/entry/whole-CV). VERBATIM user words only.
  const [copiedNote, setCopiedNote] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  async function copyOut(key: string, text: string) {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(text);
      setCopiedNote(key);
    } catch {
      setCopiedNote(`fail:${key}`);
    }
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopiedNote(null), 1500);
  }

  async function load() { const r = await api<{ model: Model }>(`/api/cvs/${cvId}/model`); if (r.ok) setModel(r.data.model); }
  async function loadTags() {
    const r = await api<{ tags: EntryTags[] }>(`/api/tags/cv/${cvId}`);
    if (r.ok) { const m: Record<string, DimTag[]> = {}; r.data.tags.forEach((t) => { m[t.entry_id] = t.dimensions; }); setTags(m); }
  }
  async function loadCoverage() { const r = await api<{ read: CoverageRead }>(`/api/coverage/cv/${cvId}`); if (r.ok) setCov(r.data.read); }
  async function loadTargets() { const r = await api<{ programs: Program[] }>(`/api/cvs/${cvId}/programs`); if (r.ok) setTargets(r.data.programs); }

  useEffect(() => { void load(); void loadTags(); void loadCoverage(); void loadTargets(); /* eslint-disable-next-line */ }, [cvId, reloadKey]);
  useEffect(() => { void (async () => { const r = await api<{ dimensions: DimRef[] }>('/api/dimensions'); if (r.ok) setDims(r.data.dimensions); })(); }, []);
  useEffect(() => { void (async () => { const r = await api<{ programs: Program[] }>(`/api/cvs/${cvId}/programs/available`); if (r.ok) setAvailable(r.data.programs); })(); /* eslint-disable-next-line */ }, [cvId]);
  // IMG-stream-first picker ordering — reuses the existing IMG-checklist read purely for its `stream` field
  // (it always reports the CV's own stream, even when applies:false / stream isn't 'img'); no new endpoint.
  useEffect(() => { void (async () => { const r = await api<{ stream?: string | null }>(`/api/coverage/cv/${cvId}/img`); if (r.ok) setCvStream(r.data.stream ?? null); })(); /* eslint-disable-next-line */ }, [cvId]);
  // Programs matching the CV's own stream sort first in BOTH pickers (main rail + drawer) — stable within
  // each group, a pure display re-sort of `available` (never alters what's fetched or what gets added).
  const availableSorted = sortProgramsStreamFirst(available, cvStream);
  useEffect(() => { void (async () => { const r = await api<{ sections: SectionRef[] }>('/api/sections'); if (r.ok) setSections(r.data.sections); })(); }, []);

  async function addTarget(programId: string) {
    if (!programId) return;
    if (gated(await api(`/api/cvs/${cvId}/programs`, { method: 'POST', body: JSON.stringify({ program_id: programId }) }))) return;
    await loadTargets();
  }
  async function removeTarget(programId: string) {
    if (gated(await api(`/api/cvs/${cvId}/programs/${programId}`, { method: 'DELETE' }))) return;
    if (activeProg === programId) { setActiveProg(null); setLens(null); setProgChar(null); }
    await loadTargets();
  }
  async function viewLens(programId: string) {
    const r = await api<{ lens: LensEntry[] }>(`/api/cvs/${cvId}/tailored/${programId}/model`);
    if (r.ok) { setLens(r.data.lens); setActiveProg(programId); }
    const c = await api<{ read: CoverageRead }>(`/api/cvs/${cvId}/tailored/${programId}/coverage`);
    setProgCov(c.ok ? c.data.read : null);
    // Program character v1 — gated OFF (or unseeded) renders nothing: an empty themes array is a valid,
    // silent result, never an error state.
    const ch = await api<CharacterResp>(`/api/cvs/${cvId}/tailored/${programId}/character`);
    setProgChar(ch.ok ? ch.data : null);
  }
  const progLabel = (p: Program) => `${[p.name, p.university, p.site].filter(Boolean).join(' · ')}${p.stream ? ` — ${p.stream}` : ''}`;

  // Program character v1 — "Themes from how this program describes its training". Renders NOTHING when the
  // endpoint is gated off or has no seeded themes (an empty array is silent, not an empty-state message).
  // The honesty-contract subline is fixed wording (never composed): not selection criteria, not a fit score,
  // not a prediction. "Related experience" is the ONLY phrase ever used for the CV linkage — never fit/alignment.
  // pc-prevalence-specialty (Sol ruling, 2026-08-04): the common set is now computed against WITHIN-SPECIALTY
  // prevalence, so it is no longer the single fixed theme the old hard-coded COMMON_THEME_LABELS map assumed —
  // it varies by specialty and can hold several themes. The line therefore ENUMERATES what collapsed and NAMES
  // the frame it collapsed against ("Common across Family Medicine programs: rural/regional training;
  // longitudinal continuity."), rather than gesturing vaguely at "common residency features". Labels come from
  // the server (THEME_SHORT_LABELS, one per released theme); a missing one falls back to plain_language
  // lower-cased — never dropped silently. Capped at 3 named themes; the rest become "and others" so the line
  // stays one readable sentence.
  const COMMON_LINE_MAX = 3;
  const commonFrame = (resp: CharacterResp) =>
    resp.specialty_label ? `Common across ${resp.specialty_label} programs` : 'Common across programs in this specialty';
  function commonThemesLine(common: CommonTheme[]): string {
    const labels = common.map((t) => t.short_label || t.plain_language.toLowerCase());
    if (labels.length === 0) return '';
    if (labels.length <= COMMON_LINE_MAX) return labels.join('; ');
    return `${labels.slice(0, COMMON_LINE_MAX).join('; ')}; and others`;
  }

  // "Themes from how this program describes its training" — renders NOTHING when the endpoint is gated off or
  // has no seeded themes AND no common features at all (an all-empty result is silent, not an empty-state
  // message). The honesty-contract subline is fixed wording (never composed): not selection criteria, not a
  // fit score, not a prediction. "Related experience" is the ONLY phrase ever used for the CV linkage.
  // pc-themes-v2 distinctiveness layer (Sol consensus §1/§4): the main list is already ordered by ascending
  // corpus prevalence (server-side) — the rarest, most distinguishing themes lead. Themes at/above 50% corpus
  // prevalence never reach `themes` at all; they're collapsed server-side into `common` and rendered as ONE
  // muted line here, consuming no card slot and carrying no related-experience links.
  //
  // ZERO-DISTINCTIVE-THEME SUPPRESSION (owner ruling + Sol option (b), 2026-08-05): when the collapse leaves NO
  // distinctive themes, the CARD IS NOT RENDERED AT ALL — not the lead, not the honesty subline, not the common
  // line. It is replaced, at this same point on the tailoring surface, by ONE plain sentence. A card-shaped
  // surface that says nothing is worse than no card on a paid feature: it spends attention and implies a finding
  // that isn't there. The copy is a NEUTRAL STATEMENT OF FACT, never an apology or an error — per the owner,
  // "if there is no differentiation, no need to invent. its then generic to the specialty. thats fine." The
  // specialty-level tailoring (the actual moat) is unaffected and still delivered, so nothing here is a failure.
  //
  // The common themes are DELIBERATELY NOT SHOWN in this branch, in any form. Sol rejected them as replacement
  // card content (it reintroduces the false-distinctiveness noise the prevalence fix removed) and permitted an
  // optional subordinate block; that option is DECLINED — rendering near-universal themes at all invites the
  // reader to mine them for a differentiator their own framing says isn't there. The sentence already conveys
  // what the list would have.
  //
  // NOTE the two empty cases are NOT the same: `themes: []` WITH a non-empty `common` means the collapse really
  // fired (the statement is earned and true), whereas `themes: []` AND `common: []` means gated-off/unseeded —
  // that stays SILENT, because we have not earned the claim that the description reflects common themes.
  function renderCharacterCard(programId: string, idSuffix: string) {
    if (!progChar || (progChar.themes.length === 0 && progChar.common.length === 0)) return null;
    const retrievedDate = progChar.retrieved_at ? new Date(progChar.retrieved_at).toLocaleDateString() : 'an earlier date';
    if (progChar.themes.length === 0) {
      return (
        <p style={S.covInform} data-testid={`pc-no-distinct-${programId}${idSuffix}`}>
          No distinctive published themes identified. This program's published description largely reflects themes
          common across {progChar.specialty_label ?? 'this specialty'}, so we won't present them as
          program-specific differentiators.
        </p>
      );
    }
    return (
      <div style={S.covPanel} data-testid={`pc-card-${programId}${idSuffix}`}>
        <p style={S.covLead}>Themes from how this program describes its training</p>
        <p style={S.covInform}>
          Derived from this program's public description, retrieved {retrievedDate}. Not selection criteria, not a
          fit score, and not a prediction — verify details on the current program page.
        </p>
        {progChar.themes.map((t) => (
          <div key={t.theme_id} style={S.covItem} data-testid={`pc-theme-${t.theme_id}${idSuffix}`}>
            <p style={S.covQ}><strong>{t.plain_language}</strong></p>
            <p style={S.covHonesty}>{t.note}</p>
            {t.related.length > 0 && (
              <p style={S.covInform} data-testid={`pc-related-${t.theme_id}${idSuffix}`}>
                Related experience on your CV: {t.related.map((r) => r.title).join('; ')}
              </p>
            )}
          </div>
        ))}
        {progChar.common.length > 0 && (
          <p style={S.covInform} data-testid={`pc-common-line-${programId}${idSuffix}`}>
            <strong>{commonFrame(progChar)}:</strong> {commonThemesLine(progChar.common)}.
          </p>
        )}
      </div>
    );
  }

  async function reread() {
    setInferring(true);
    await api(`/api/tags/cv/${cvId}/infer`, { method: 'POST' });
    await loadTags(); await loadCoverage();
    setInferring(false);
  }
  async function addTag(entryId: string, dimId: string) {
    await api(`/api/tags/entry/${entryId}`, { method: 'POST', body: JSON.stringify({ dimension_id: dimId }) });
    setAddingFor(null); await loadTags(); await loadCoverage();
  }
  async function removeTag(entryId: string, dimId: string) {
    await api(`/api/tags/entry/${entryId}/${dimId}`, { method: 'DELETE' });
    await loadTags(); await loadCoverage();
  }

  function startEdit(b: MBullet) { setEditing(b.id); setDraft(b.text); setDiag(null); }
  // cc-11 round 18 (Sol round-17 blocker 1) — `section` MUST be the entry's TAXONOMY section id. This used to be
  // called with the enclosing `sec.id`, which on this model is a synthetic OUTPUT-section id (`out:Research &
  // Publications`, a merged heading covering three taxonomy sections). The prompt promises the model "always its
  // section", and the research null-router arms on `section === 'research-and-scholarly-activity'` — a pseudo-id
  // satisfied neither. Callers pass `e.section`.
  async function check(section: string, archetype: string) {
    const r = await api<{ result: ClassifyResult }>('/api/classify', { method: 'POST', body: JSON.stringify({ bullet_text: draft, section, archetype }) });
    if (r.ok) setDiag(r.data.result);
  }
  async function saveBullet(id: string) { if (gated(await api(`/api/bullets/${id}`, { method: 'PATCH', body: JSON.stringify({ text: draft }) }))) return; setEditing(null); setDiag(null); await load(); }
  async function doDelete() {
    if (!pendingDel) return;
    if (gated(await api(`/api/${pendingDel.kind === 'bullet' ? 'bullets' : 'entries'}/${pendingDel.id}`, { method: 'DELETE' }))) { setPendingDel(null); return; }
    setPendingDel(null); await load();
  }
  async function moveBullet(e: MEntry, i: number, dir: number) {
    const j = i + dir; if (j < 0 || j >= e.bullets.length) return;
    if (gated(await api(`/api/bullets/${e.bullets[i].id}`, { method: 'PATCH', body: JSON.stringify({ sort_order: j }) }))) return;
    await api(`/api/bullets/${e.bullets[j].id}`, { method: 'PATCH', body: JSON.stringify({ sort_order: i }) });
    await load();
  }
  async function moveEntry(entries: MEntry[], i: number, dir: number) {
    const j = i + dir; if (j < 0 || j >= entries.length) return;
    if (gated(await api(`/api/entries/${entries[i].id}`, { method: 'PATCH', body: JSON.stringify({ sort_order: j }) }))) return;
    await api(`/api/entries/${entries[j].id}`, { method: 'PATCH', body: JSON.stringify({ sort_order: i }) });
    await load();
  }

  // "+ Add a line" — POST the new create-bullet endpoint; 402 keeps the draft on screen.
  function startAddBullet(entryId: string) { setAddingBulletFor(entryId); setAddDraft(''); setAddNote(''); }
  async function saveAddBullet(entryId: string) {
    const text = addDraft.trim();
    if (!text) { setAddNote('Nothing to save — the line is empty.'); return; }
    const r = await api(`/api/entries/${entryId}/bullets`, { method: 'POST', body: JSON.stringify({ text }) });
    if (r.status === 402 && (r.data as { reason?: string } | null)?.reason === 'needs_paid_cv') {
      onPaywall?.(cvId); setAddNote('This CV needs its unlock to save — your words are kept; try again after.'); return;
    }
    if (!r.ok) { setAddNote('That didn’t save — check your connection and try again.'); return; }
    setAddingBulletFor(null); setAddDraft(''); setAddNote(''); await load();
  }

  // Entry field editor — lazy-loads the full row (section/start_date/end_date/date_text aren't in the render model).
  async function startEntryEdit(e: MEntry) {
    setEntryEdit(e.id); setEntryArchetype(e.archetype);
    const r = await api<{ entry: EntryFull }>(`/api/entries/${e.id}`);
    if (r.ok) {
      const full = r.data.entry;
      setEntryDraft({
        title: full.title ?? '', organization: full.organization ?? '',
        start_date: full.start_date ?? '', end_date: full.end_date ?? '', date_text: full.date_text ?? '',
        section: full.section,
      });
    } else {
      setEntryDraft({ title: e.title ?? '', organization: e.organization ?? '', start_date: '', end_date: '', date_text: e.dateText ?? '', section: '' });
    }
  }
  async function saveEntryEdit(entryId: string) {
    if (gated(await api(`/api/entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        title: entryDraft.title, organization: entryDraft.organization,
        start_date: entryDraft.start_date || null, end_date: entryDraft.end_date || null, date_text: entryDraft.date_text || null,
        section: entryDraft.section,
      }),
    }))) return;
    setEntryEdit(null); await load();
  }

  async function downloadCanonical() {
    setExportNote('');
    const q = exportCrafted ? '?crafted=1' : '';
    const res = await fetch(`/api/cvs/${cvId}/export.${exportFormat}${q}`, { credentials: 'include' });
    if (res.status === 402) { onPaywall?.(cvId); setExportNote('This CV needs its unlock to download; your entries are unaffected.'); return; }
    if (!res.ok) { setExportNote(`export failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `cv.${exportFormat}`; document.body.appendChild(link); link.click();
    link.remove(); URL.revokeObjectURL(url);
  }

  function CopyNote({ forKey }: { forKey: string }) {
    if (copiedNote === forKey) return <span style={S.copyNote} data-testid="ws-copied-note"><IconCheck aria-hidden="true" /> Copied</span>;
    if (copiedNote === `fail:${forKey}`) return <span style={S.copyNote} data-testid="ws-copied-note">Copy isn't available here — select the text directly.</span>;
    return null;
  }

  if (!model) return <main style={S.main}><p>Loading your CV…</p></main>;

  return (
    <main style={S.main} data-testid="review">
      {/* Polish B2 mobile tranche — hover-reveal controls only make sense where hover exists. At narrow/touch
          widths they render always-visible instead (no hover affordance on touch), with larger tap targets. */}
      <style>{narrow ? `
        .ws-entry .ws-controls, .ws-bullet .ws-controls { opacity: 1; }
        .ws-controls button, .ws-controls a { min-height: 44px; min-width: 44px; padding: 10px 12px; }
      ` : `
        .ws-entry .ws-controls, .ws-bullet .ws-controls { opacity: 0; transition: opacity .12s; }
        .ws-entry:hover .ws-controls, .ws-entry:focus-within .ws-controls,
        .ws-bullet:hover .ws-controls, .ws-bullet:focus-within .ws-controls { opacity: 1; }
      `}</style>
      <button className="btn btn-ghost" style={{ marginLeft: 0, paddingLeft: 0, marginBottom: 'var(--space-2)' }} onClick={onBack} data-testid="back-to-author"><IconBack aria-hidden="true" /> Back to authoring</button>
      <h1>Your CV</h1>
      <p style={S.contact}>
        <strong>{model.contact.name}</strong><br />
        {[model.contact.email, model.contact.phone, model.contact.city].filter(Boolean).join('  •  ')}
      </p>

      <div style={S.toolbar} data-testid="ws-toolbar">
        <label style={{ fontSize: 'var(--fs-small)' }}>Format
          <select style={{ ...S.addSel, marginLeft: 6 }} value={exportFormat} onChange={(e) => setExportFormat(e.target.value as 'docx' | 'pdf')} data-testid="ws-export-format">
            <option value="docx">Word (.docx)</option>
            <option value="pdf">PDF</option>
          </select>
        </label>
        <label style={{ fontSize: 'var(--fs-small)' }}>
          <input type="checkbox" checked={exportCrafted} onChange={(e) => setExportCrafted(e.target.checked)} data-testid="ws-export-crafted" /> add "Crafted [date]"
        </label>
        <button className="btn btn-primary" style={S.smallBtn} onClick={() => void downloadCanonical()} data-testid="ws-download"><IconDownload aria-hidden="true" /> Download</button>
        {exportNote && <span style={S.note} data-testid="ws-export-note">{exportNote}</span>}
        <button style={S.mini} onClick={() => void copyOut('all', formatCv(model))} data-testid="ws-copy-all"><IconCopy aria-hidden="true" /> Copy all as text</button>
        <CopyNote forKey="all" />
      </div>

      <div style={S.coverage} data-testid="coverage-bar">
        <button style={S.mini} onClick={reread} disabled={inferring} data-testid="reread-coverage">
          {inferring ? 'Reading…' : 'Re-read coverage'}
        </button>
        <span style={S.tag}>I read each entry for the focus areas it shows — correct anything below that I got wrong.</span>
      </div>

      {cov && cov.hasContent && (
        <div style={S.covPanel} data-testid="coverage-panel">
          <p style={S.covLead} data-testid="cov-lead">{cov.lead}</p>
          {cov.recover.map((r) => (
            <div key={r.dimension_id} style={S.covItem} data-testid={`cov-recover-${r.dimension_id}`}>
              <p style={S.covLess}>{r.lessLine}</p>
              {r.where && <p style={S.covWhere}>{r.where}</p>}
              <p style={S.covQ}>{r.recoverQuestion}</p>
              <p style={S.covHonesty}>{r.honesty}</p>
              <p style={S.covInform}>{r.inform}</p>
              <button style={S.link} onClick={() => addForGap(r.dimension_id)} data-testid={`cov-add-${r.dimension_id}`}><IconAdd aria-hidden="true" /> Add an entry for this</button>
            </div>
          ))}
        </div>
      )}

      {/* Polish B2 mobile tranche — under 900px the tailoring rail moves into a Drawer opened from this button
          (a visible, always-present opener), instead of stacking below the whole CV. */}
      {narrow && (
        <button className="btn btn-secondary" style={S.tailorOpenBtn} onClick={() => setTailorOpen(true)} data-testid="ws-tailor-drawer-open">
          Tailoring{targets.length > 0 ? ` (${targets.length})` : ''}
        </button>
      )}

      <div style={narrow ? S.layoutNarrow : S.layout}>
        <div style={S.paperCol}>
          {model.sections.map((sec) => (
            <div key={sec.id} style={S.section} data-testid={`rsec-${sec.id}`}>
              <h3 style={S.h3}>
                {sec.name} <span style={S.tag}>{sec.entries[0]?.archetype}</span>
                <button style={S.mini} onClick={() => void copyOut(`sec-${sec.id}`, formatSection(sec))} data-testid={`ws-copy-section-${sec.id}`}><IconCopy aria-hidden="true" /> copy section</button>
                <CopyNote forKey={`sec-${sec.id}`} />
              </h3>
              {sec.entries.map((e, ei) => {
                const sameArchetype = sections.filter((s) => s.archetype === e.archetype);
                return (
                  <div key={e.id} className="ws-entry" style={S.entry} data-testid={`rentry-${e.id}`}>
                    <div style={S.entryHead}>
                      {entryEdit === e.id ? (
                        <div style={S.editBox} data-testid={`ws-entry-edit-${e.id}`}>
                          <label style={S.fieldLabel}>Title
                            <input style={S.editInput} value={entryDraft.title} onChange={(ev) => setEntryDraft({ ...entryDraft, title: ev.target.value })} data-testid={`ws-entry-title-${e.id}`} />
                          </label>
                          <label style={S.fieldLabel}>Organization
                            <input style={S.editInput} value={entryDraft.organization} onChange={(ev) => setEntryDraft({ ...entryDraft, organization: ev.target.value })} data-testid={`ws-entry-org-${e.id}`} />
                          </label>
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <label style={S.fieldLabel}>Start
                              <input style={S.editInput} value={entryDraft.start_date} onChange={(ev) => setEntryDraft({ ...entryDraft, start_date: ev.target.value })} data-testid={`ws-entry-dates-start-${e.id}`} />
                            </label>
                            <label style={S.fieldLabel}>End
                              <input style={S.editInput} value={entryDraft.end_date} onChange={(ev) => setEntryDraft({ ...entryDraft, end_date: ev.target.value })} data-testid={`ws-entry-dates-end-${e.id}`} />
                            </label>
                          </div>
                          <label style={S.fieldLabel}>Date text
                            <input style={S.editInput} value={entryDraft.date_text} onChange={(ev) => setEntryDraft({ ...entryDraft, date_text: ev.target.value })} data-testid={`ws-entry-dates-text-${e.id}`} />
                          </label>
                          <p style={S.hint}>single or open-ended dates go here</p>
                          <label style={S.fieldLabel}>Section
                            <select style={S.addSel} value={entryDraft.section} onChange={(ev) => setEntryDraft({ ...entryDraft, section: ev.target.value })} data-testid={`ws-section-move-${e.id}`}>
                              {sameArchetype.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </label>
                          <div>
                            <button style={S.mini} onClick={() => void saveEntryEdit(e.id)} data-testid={`ws-entry-save-${e.id}`}>Save</button>
                            <button style={S.mini} onClick={() => setEntryEdit(null)} data-testid={`ws-entry-cancel-${e.id}`}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <span>
                          {/* FIX 4 — an entry with no title (or whitespace only) used to render a BLANK title span
                              with a floating "Edit" button after it (seen on Languages entries). A muted placeholder
                              names the gap and says what to do, matching QualityReadView's fallback pattern. */}
                          {e.title?.trim()
                            ? <strong data-testid={`ws-entry-title-view-${e.id}`}>{e.title}</strong>
                            : <strong style={S.untitled} data-testid={`ws-entry-title-view-${e.id}`}>(untitled — add a title)</strong>}
                          {e.organization ? `, ${e.organization}` : ''}{e.dateText ? `  — ${e.dateText}` : ''}
                          <span className="ws-controls" style={S.controls}>
                            <button style={S.mini} onClick={() => void startEntryEdit(e)} data-testid={`edit-entry-${e.id}`}>Edit</button>
                          </span>
                        </span>
                      )}
                      <span className="ws-controls" style={S.controls}>
                        <button style={S.mini} onClick={() => moveEntry(sec.entries, ei, -1)} title="up" aria-label="Move entry up"><IconUp aria-hidden="true" /></button>
                        <button style={S.mini} onClick={() => moveEntry(sec.entries, ei, 1)} title="down" aria-label="Move entry down"><IconDown aria-hidden="true" /></button>
                        <button style={S.mini} onClick={() => void copyOut(`entry-${e.id}`, formatEntry(e))} data-testid={`ws-copy-entry-${e.id}`}><IconCopy aria-hidden="true" /> copy</button>
                        <button style={S.miniDanger} onClick={() => setPendingDel({ kind: 'entry', id: e.id })} data-testid={`del-entry-${e.id}`}><IconTrash aria-hidden="true" /> Delete entry</button>
                      </span>
                    </div>
                    <CopyNote forKey={`entry-${e.id}`} />

                    <div style={S.dimStrip} data-testid={`dims-${e.id}`}>
                      {(tags[e.id] ?? []).map((d) => (
                        <span key={d.id} style={S.chip} title={d.plain_language} data-testid={`chip-${e.id}-${d.id}`}>
                          {d.name}
                          <button style={S.chipX} onClick={() => removeTag(e.id, d.id)} title="remove" aria-label="Remove tag" data-testid={`chip-del-${e.id}-${d.id}`}><IconClose aria-hidden="true" /></button>
                        </span>
                      ))}
                      {addingFor === e.id ? (
                        <select autoFocus style={S.addSel} defaultValue="" data-testid={`add-dim-${e.id}`}
                          onChange={(ev) => { if (ev.target.value) void addTag(e.id, ev.target.value); }}>
                          <option value="" disabled>add a focus…</option>
                          {dims.filter((d) => !(tags[e.id] ?? []).some((t) => t.id === d.id)).map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      ) : (
                        <button style={S.chipAdd} onClick={() => setAddingFor(e.id)} data-testid={`add-chip-${e.id}`}>+ add</button>
                      )}
                    </div>

                    <ul style={{ margin: 'var(--space-1) 0' }}>
                      {e.bullets.map((b, bi) => (
                        <li key={b.id} className="ws-bullet" data-testid={`rbullet-${b.id}`}>
                          {editing === b.id ? (
                            <div>
                              <textarea style={S.ta} rows={2} value={draft} onChange={(ev) => setDraft(ev.target.value)} data-testid="edit-text" />
                              <div>
                                <button style={S.mini} onClick={() => check(e.section, e.archetype)} data-testid="edit-check">Check</button>
                                <button style={S.mini} onClick={() => saveBullet(b.id)} data-testid="edit-save">Save</button>
                                <button style={S.mini} onClick={() => { setEditing(null); setDiag(null); }}>Cancel</button>
                              </div>
                              {diag && (
                                <div style={S.diag} data-testid="edit-diag">
                                  {diag.overall === 'pass' ? <span><IconCheck aria-hidden="true" /> Nothing flagged.</span> : (
                                    <ul>
                                      {diag.ladder.status === 'flag' && <li>{diag.ladder.diagnosis}<br />{diag.ladder.question}
                                        {isQuantityAsk(diag.ladder.question) && <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>}
                                      </li>}
                                      {diag.integrity.map((g) => <li key={g.gate_id}>{g.diagnosis}<br />{g.question}
                                        {isQuantityAsk(g.question) && <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>}
                                      </li>)}
                                    </ul>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span>
                              {b.text}
                              <span className="ws-controls" style={S.controls}>
                                <button style={S.mini} onClick={() => startEdit(b)} data-testid={`edit-${b.id}`}>edit</button>
                                <button style={S.mini} onClick={() => moveBullet(e, bi, -1)} title="up" aria-label="Move bullet up"><IconUp aria-hidden="true" /></button>
                                <button style={S.mini} onClick={() => moveBullet(e, bi, 1)} title="down" aria-label="Move bullet down"><IconDown aria-hidden="true" /></button>
                                <button style={S.miniDanger} onClick={() => setPendingDel({ kind: 'bullet', id: b.id })} data-testid={`del-bullet-${b.id}`} aria-label="Delete bullet"><IconTrash aria-hidden="true" /></button>
                              </span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>

                    {addingBulletFor === e.id ? (
                      <div style={S.editBox}>
                        <textarea style={S.ta} rows={2} value={addDraft} onChange={(ev) => setAddDraft(ev.target.value)} data-testid={`ws-add-bullet-text-${e.id}`} />
                        <div>
                          <button style={S.mini} onClick={() => void saveAddBullet(e.id)} data-testid={`ws-add-bullet-save-${e.id}`}>Save</button>
                          <button style={S.mini} onClick={() => setAddingBulletFor(null)} data-testid={`ws-add-bullet-cancel-${e.id}`}>Cancel</button>
                        </div>
                        {addNote && <p style={S.note} data-testid={`ws-add-bullet-note-${e.id}`}>{addNote}</p>}
                      </div>
                    ) : (
                      <button style={S.chipAdd} onClick={() => startAddBullet(e.id)} data-testid={`ws-add-bullet-${e.id}`}>+ Add a line</button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {!narrow && <div style={S.rail}>
          <div style={S.tailorPanel} data-testid="tailor-panel">
            <h3 style={S.h3}>Tailor for a program</h3>
            <p style={S.tag}>Pick the programs you're applying to. Each gets a CV arranged to lead with your most relevant entries — your words, reordered, never rewritten. Your default download stays chronological.</p>
            <div style={S.coverage}>
              <select defaultValue="" style={S.addSel} data-testid="program-picker" onChange={(ev) => { if (ev.target.value) { void addTarget(ev.target.value); ev.target.value = ''; } }}>
                <option value="" disabled>add a target program…</option>
                {availableSorted.filter((p) => !targets.some((t) => t.program_id === p.program_id)).map((p) => (
                  <option key={p.program_id} value={p.program_id}>{progLabel(p)}</option>
                ))}
              </select>
              <span style={S.tag}>{available.length} programs in your specialty</span>
            </div>
            {targets.length > 0 && (
              <div style={{ ...S.coverage, fontSize: 'var(--fs-small)' }} data-testid="tailored-export-opts">
                <label>Tailored download:
                  <select style={{ ...S.addSel, marginLeft: 6 }} value={tlFormat} onChange={(e) => setTlFormat(e.target.value as 'docx' | 'pdf')} data-testid="tl-format">
                    <option value="docx">Word (.docx)</option>
                    <option value="pdf">PDF</option>
                  </select>
                </label>
                <label><input type="checkbox" checked={tlCrafted} onChange={(e) => setTlCrafted(e.target.checked)} data-testid="tl-crafted" /> add "Crafted [date]"</label>
              </div>
            )}
            {targets.map((p) => (
              <div key={p.program_id} style={S.covItem} data-testid={`target-${p.program_id}`}>
                <div style={S.targetHead}>
                  <strong style={S.targetName}>{progLabel(p)}</strong>
                  <span style={S.targetControls}>
                    <button style={S.mini} onClick={() => viewLens(p.program_id)} data-testid={`lens-${p.program_id}`}>Relevance lens</button>
                    <a style={S.mini} href={`/api/cvs/${cvId}/tailored/${p.program_id}/export.${tlFormat}${tlCrafted ? '?crafted=1' : ''}`} data-testid={`dl-${p.program_id}`}>Download tailored ({tlFormat})</a>
                    <button style={S.miniDanger} onClick={() => removeTarget(p.program_id)} data-testid={`untarget-${p.program_id}`}>remove</button>
                  </span>
                </div>
                {activeProg === p.program_id && lens && (
                  <div style={S.covInform} data-testid={`lens-out-${p.program_id}`}>
                    Leading with: {lens.filter((l) => l.foregrounded).length
                      ? leadingWithLabels(model, lens)
                      : 'no tagged entries yet — tag your entries above to sharpen tailoring.'}
                    {' '}(arranged within sections by relevance — not strictly chronological.)
                  </div>
                )}
                {activeProg === p.program_id && progCov && progCov.hasContent && (
                  <div style={S.covPanel} data-testid={`prog-cov-${p.program_id}`}>
                    <p style={S.tag}>Coverage for this program</p>
                    <p style={S.covLead} data-testid={`prog-cov-lead-${p.program_id}`}>{progCov.lead}</p>
                    {progCov.recover.map((r) => (
                      <div key={r.dimension_id} style={S.covItem} data-testid={`prog-cov-recover-${p.program_id}-${r.dimension_id}`}>
                        <p style={S.covLess}>{r.lessLine}</p>
                        {r.where && <p style={S.covWhere}>{r.where}</p>}
                        <p style={S.covQ}>{r.recoverQuestion}</p>
                        <p style={S.covHonesty}>{r.honesty}</p>
                        <p style={S.covInform}>{r.inform}</p>
                        <button style={S.link} onClick={() => addForGap(r.dimension_id)} data-testid={`prog-cov-add-${p.program_id}-${r.dimension_id}`}><IconAdd aria-hidden="true" /> Add an entry for this</button>
                      </div>
                    ))}
                  </div>
                )}
                {activeProg === p.program_id && renderCharacterCard(p.program_id, '')}
              </div>
            ))}
          </div>
        </div>}
      </div>

      {narrow && (
        <Drawer open={tailorOpen} onClose={() => setTailorOpen(false)} label="Tailoring" testId="ws-tailor-drawer">
          <div style={S.tailorPanel} data-testid="tailor-panel">
            <h3 style={S.h3}>Tailor for a program</h3>
            <p style={S.tag}>Pick the programs you're applying to. Each gets a CV arranged to lead with your most relevant entries — your words, reordered, never rewritten. Your default download stays chronological.</p>
            <div style={S.coverage}>
              <select defaultValue="" style={S.addSel} data-testid="program-picker-drawer" onChange={(ev) => { if (ev.target.value) { void addTarget(ev.target.value); ev.target.value = ''; } }}>
                <option value="" disabled>add a target program…</option>
                {availableSorted.filter((p) => !targets.some((t) => t.program_id === p.program_id)).map((p) => (
                  <option key={p.program_id} value={p.program_id}>{progLabel(p)}</option>
                ))}
              </select>
              <span style={S.tag}>{available.length} programs in your specialty</span>
            </div>
            {targets.length > 0 && (
              <div style={{ ...S.coverage, fontSize: 'var(--fs-small)' }} data-testid="tailored-export-opts-drawer">
                <label>Tailored download:
                  <select style={{ ...S.addSel, marginLeft: 6 }} value={tlFormat} onChange={(e) => setTlFormat(e.target.value as 'docx' | 'pdf')} data-testid="tl-format-drawer">
                    <option value="docx">Word (.docx)</option>
                    <option value="pdf">PDF</option>
                  </select>
                </label>
                <label><input type="checkbox" checked={tlCrafted} onChange={(e) => setTlCrafted(e.target.checked)} data-testid="tl-crafted-drawer" /> add "Crafted [date]"</label>
              </div>
            )}
            {targets.map((p) => (
              <div key={p.program_id} style={S.covItem} data-testid={`target-drawer-${p.program_id}`}>
                <div style={S.targetHead}>
                  <strong style={S.targetName}>{progLabel(p)}</strong>
                  <span style={S.targetControls}>
                    <button style={S.mini} onClick={() => viewLens(p.program_id)} data-testid={`lens-drawer-${p.program_id}`}>Relevance lens</button>
                    <a style={S.mini} href={`/api/cvs/${cvId}/tailored/${p.program_id}/export.${tlFormat}${tlCrafted ? '?crafted=1' : ''}`} data-testid={`dl-drawer-${p.program_id}`}>Download tailored ({tlFormat})</a>
                    <button style={S.miniDanger} onClick={() => removeTarget(p.program_id)} data-testid={`untarget-drawer-${p.program_id}`}>remove</button>
                  </span>
                </div>
                {activeProg === p.program_id && lens && (
                  <div style={S.covInform} data-testid={`lens-out-drawer-${p.program_id}`}>
                    Leading with: {lens.filter((l) => l.foregrounded).length
                      ? leadingWithLabels(model, lens)
                      : 'no tagged entries yet — tag your entries above to sharpen tailoring.'}
                    {' '}(arranged within sections by relevance — not strictly chronological.)
                  </div>
                )}
                {activeProg === p.program_id && progCov && progCov.hasContent && (
                  <div style={S.covPanel} data-testid={`prog-cov-drawer-${p.program_id}`}>
                    <p style={S.tag}>Coverage for this program</p>
                    <p style={S.covLead} data-testid={`prog-cov-lead-drawer-${p.program_id}`}>{progCov.lead}</p>
                    {progCov.recover.map((r) => (
                      <div key={r.dimension_id} style={S.covItem} data-testid={`prog-cov-recover-drawer-${p.program_id}-${r.dimension_id}`}>
                        <p style={S.covLess}>{r.lessLine}</p>
                        {r.where && <p style={S.covWhere}>{r.where}</p>}
                        <p style={S.covQ}>{r.recoverQuestion}</p>
                        <p style={S.covHonesty}>{r.honesty}</p>
                        <p style={S.covInform}>{r.inform}</p>
                        <button style={S.link} onClick={() => addForGap(r.dimension_id)} data-testid={`prog-cov-add-drawer-${p.program_id}-${r.dimension_id}`}><IconAdd aria-hidden="true" /> Add an entry for this</button>
                      </div>
                    ))}
                  </div>
                )}
                {activeProg === p.program_id && renderCharacterCard(p.program_id, '-drawer')}
              </div>
            ))}
          </div>
        </Drawer>
      )}

      {pendingDel && (
        <div style={S.confirm} data-testid="confirm-delete">
          Delete this {pendingDel.kind}{pendingDel.kind === 'entry' ? ' and its bullets' : ''}?
          <button style={S.miniDanger} onClick={doDelete} data-testid="confirm-yes">Yes, delete</button>
          <button style={S.mini} onClick={() => setPendingDel(null)}>Cancel</button>
        </div>
      )}
    </main>
  );
}


// The "Leading with" preview names the entries a program's tailored CV opens with. It used to join RAW titles, so a CV
// with repeated roles read "General Practitioner; General Practitioner; General Practitioner" — the same defect the
// character card's related-experience list had. Same fix: the shipped org-by-default label rule, then de-duplicate
// identical labels (found in the live pass, 2026-08-04).
export function leadingWithLabels(
  model: { sections: { entries: { id: string; title: string | null; organization: string | null }[] }[] },
  lens: LensEntry[],
): string {
  const labels = model.sections
    .flatMap((s) => s.entries)
    .filter((e) => lens.find((l) => l.entry_id === e.id)?.foregrounded)
    .map((e) => entryLabel(e))
    .filter((x) => x.trim().length > 0);
  return [...new Set(labels)].join('; ');
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 1040, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  link: { background: 'none', border: 'none', color: 'var(--ink-subtle)', cursor: 'pointer', padding: 0, marginBottom: 'var(--space-2)' },
  contact: { color: 'var(--ink)' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap', background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 8, padding: 'var(--space-3) var(--space-4)', margin: 'var(--space-3) 0' },
  smallBtn: { fontSize: 'var(--fs-small)', padding: 'var(--space-1) var(--space-3)' },
  layout: { display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start' },
  layoutNarrow: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' },
  paperCol: { flex: '1 1 620px', minWidth: 0, fontFamily: 'var(--serif)' },
  rail: { flex: '0 0 320px', minWidth: 0 },
  section: { margin: 'var(--space-4) 0' },
  h3: { borderBottom: '1px solid var(--rule)', paddingBottom: 2, fontFamily: 'var(--sans)' },
  tag: { fontSize: 'var(--fs-label)', color: 'var(--muted)', fontWeight: 400 },
  // FIX 4 — the empty-title placeholder: present enough to be actionable, quiet enough not to read as content.
  untitled: { color: 'var(--muted)', fontWeight: 400, fontStyle: 'italic' },
  entry: { margin: 'var(--space-2) 0' },
  // FIX 2 — the head row WRAPS. It used to be a non-wrapping flex row holding a nowrap controls span: at the
  // rail/drawer's width (~700–900px viewports) the controls could neither wrap nor shrink, so they overflowed
  // the panel (clipping "Download tailored (docx)" and raising a horizontal PAGE scrollbar) while squeezing the
  // name beside them to min-content — which wrapped it one word per line ("Family / Medicine / · / Dalhousie").
  entryHead: { display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)', flexWrap: 'wrap', maxWidth: '100%', minWidth: 0 },
  controls: { whiteSpace: 'nowrap' },
  // The TAILOR-TARGET row's own pieces (the narrow rail/drawer context). The name takes a full line and breaks on
  // sensible boundaries; the controls wrap onto the next line instead of running off the edge.
  targetHead: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 'var(--space-2)', maxWidth: '100%', minWidth: 0 },
  targetName: { flex: '1 1 100%', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'normal' },
  targetControls: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-1)', minWidth: 0, maxWidth: '100%', whiteSpace: 'normal' },
  mini: { fontSize: 'var(--fs-label)', marginLeft: 4, cursor: 'pointer' },
  miniDanger: { fontSize: 'var(--fs-label)', marginLeft: 4, cursor: 'pointer', color: 'var(--danger)' },
  ta: { width: '100%', fontFamily: 'inherit', padding: 'var(--space-2)' },
  diag: { background: 'var(--warn-surface)', padding: 'var(--space-2)', borderRadius: 6, marginTop: 'var(--space-2)', fontSize: 'var(--fs-body)' },
  // cc-11 round 14 — same treatment as QualityReadView's `quantityHint`: quiet, italic, clearly product copy.
  quantityHint: { margin: 'var(--space-1) 0 0', color: 'var(--ink-subtle)', fontSize: 'var(--fs-small)', fontStyle: 'italic' },
  confirm: { position: 'sticky', bottom: 0, background: 'var(--danger-surface)', padding: 'var(--space-3)', borderRadius: 6, marginTop: 'var(--space-3)' },
  // FIX 2 — minWidth:0 lets a flex CHILD (the program picker) shrink below its intrinsic content width instead of
  // forcing the row wider than the panel.
  coverage: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', margin: 'var(--space-3) 0 var(--space-1)', flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' },
  dimStrip: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', margin: 'var(--space-1) 0 2px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--fs-label)', background: 'var(--surface-subtle)', color: 'var(--ink)', border: '1px solid var(--rule)', borderRadius: 12, padding: '1px var(--space-2) 1px var(--space-2)' },
  chipX: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 'var(--fs-body)', lineHeight: 1, padding: 0 },
  chipAdd: { fontSize: 'var(--fs-label)', background: 'none', border: '1px dashed var(--rule)', borderRadius: 12, padding: '1px var(--space-2)', color: 'var(--ink-subtle)', cursor: 'pointer' },
  // FIX 2 — a <select> sizes itself to its WIDEST option: the program picker's 237 long program labels made it
  // 554px wide inside a 361px drawer, which scrolled the drawer horizontally (found by driving the real browser
  // at 800px — the style-token tests could not see it). Bounded to its container; the open list is unaffected.
  addSel: { fontSize: 'var(--fs-label)', padding: '1px var(--space-1)', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' },
  covPanel: { background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 8, padding: 'var(--space-3) var(--space-4)', margin: 'var(--space-2) 0 var(--space-4)' },
  covLead: { margin: '0 0 var(--space-2)', fontWeight: 600, color: 'var(--ink)' },
  covItem: { borderTop: '1px solid var(--rule)', paddingTop: 10, marginTop: 'var(--space-3)' },
  covLess: { margin: '0 0 var(--space-1)', color: 'var(--ink)' },
  covWhere: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-small)', color: 'var(--ink-subtle)' },
  covQ: { margin: '0 0 var(--space-1)', color: 'var(--ink)' },
  covHonesty: { margin: '0 0 var(--space-1)', fontSize: 'var(--fs-small)', color: 'var(--muted)' },
  covInform: { margin: '0 0 var(--space-2)', fontSize: 'var(--fs-small)', color: 'var(--muted)', fontStyle: 'italic' },
  // FIX 2 — width-bounded to its container (the 320px rail, or the drawer's min(85vw,360px) body) so nothing it
  // holds can push the page into a horizontal scroll. border-box keeps the padding inside that bound.
  tailorPanel: { background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 8, padding: 'var(--space-3) var(--space-4)', margin: 'var(--space-2) 0 var(--space-4)', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflowWrap: 'break-word' },
  tailorOpenBtn: { display: 'block', margin: 'var(--space-3) 0', minHeight: 44, fontSize: 'var(--fs-body)' },
  editBox: { fontFamily: 'var(--sans)', margin: 'var(--space-1) 0', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  editInput: { display: 'block', width: '100%', boxSizing: 'border-box', padding: 'var(--space-2)', fontSize: 'var(--fs-body)' },
  fieldLabel: { fontSize: 'var(--fs-label)', color: 'var(--muted)', display: 'block' },
  hint: { fontSize: 'var(--fs-micro)', color: 'var(--muted)', margin: '0 0 var(--space-1)', fontStyle: 'italic' },
  note: { fontFamily: 'var(--sans)', fontSize: 'var(--fs-label)', color: 'var(--warning)', margin: 'var(--space-2) 0 0' },
  copyNote: { fontSize: 'var(--fs-label)', color: 'var(--muted)', marginLeft: 6 },
};
